@if (navState$ | async; as expertsExpanded) {
  <table
    mat-table
    matSort
    matSortDirection="desc"
    matSortStart="desc"
    multiTemplateDataRows
    class="full-width"
    [matSortActive]="dateSortColumn.key"
    [dataSource]="expertsFiltered"
    [trackBy]="trackByExpertId"
  >
    <ng-container matColumnDef="dropDown">
      <th *matHeaderCellDef mat-header-cell>
        <app-expert-expand-all-toggle
          [collapsed]="showCollapsed"
          (toggleSignal)="onToggleExpandAll(expertsExpanded)"
        >
        </app-expert-expand-all-toggle>
      </th>
      <td
        *matCellDef="let element"
        mat-cell
        data-test-id="expert-table-expand-expert"
      >
        <mat-icon>
          {{
            expertsExpanded[element.expertId]
              ? 'keyboard_arrow_up'
              : 'keyboard_arrow_down'
          }}
        </mat-icon>
      </td>
    </ng-container>
    <ng-container matColumnDef="approval">
      <th *matHeaderCellDef mat-header-cell mat-sort-header="clientApproved">
        <mat-icon class="material-icons" matTooltip="Sort by approval status">
          lock_person
        </mat-icon>
      </th>
      <td
        *matCellDef="let element"
        mat-cell
        data-test-id="expert-table-clientApproved"
      >
        <div class="flex gap-2">
          <div
            [matTooltip]="
              element.clientApproved | approvalTooltip: selectedTimezone()
            "
          >
            <mat-icon
              class="material-icons-outlined approval-lock"
              data-test-id="expert-table-approval-icon"
              [class.approved]="element.clientApproved"
              [class.not-approved]="!element.clientApproved"
            >
              {{ element.clientApproved ? 'lock_open' : 'lock' }}
            </mat-icon>
          </div>
        </div>
      </td>
    </ng-container>
    @for (column of expertColumns; track column.key) {
      <ng-container [matColumnDef]="column.key">
        <th *matHeaderCellDef mat-header-cell [mat-sort-header]="column.key">
          {{ column.name }}
        </th>
        <td
          *matCellDef="let element"
          mat-cell
          [attr.data-test-id]="'expert-table-' + column.key"
        >
          @if (!hideExpertDataValue(column.key)) {
            <span
              [class.is-bold]="
                !canMarkExpertsAsSeen ||
                (!element.seenByClient && canMarkExpertsAsSeen)
              "
            >
              {{ element | expertListValue: column.key : selectedTimezone() }}
            </span>
          }
          @if (column.key === 'screeningStatus') {
            <div [style.min-width]="'100px'">
              @if (element.screeningStatus === 'complete-near-pass') {
                <div
                  class="flex justify-start items-center gap-2"
                  matTooltip="Expert failed screener but was a near pass"
                  data-test-id="expert-table-near-pass-icon"
                >
                  <mat-icon class="material-icons-outlined near-pass">
                    call_missed
                  </mat-icon>
                  <span
                    class="near-pass"
                    data-test-id="expert-table-near-pass-icon-label"
                  >
                    Near pass
                  </span>
                </div>
              }
            </div>
          }
          @if (column.key === 'unitsPerHour') {
            <app-expert-unit [expert]="element"></app-expert-unit>
          }
          @if (
            column.key === 'requestedAnotherTime' &&
            element.requestedAnotherTime
          ) {
            <mat-icon
              class="text-orange-400"
              matTooltip="Consultation time request in progress"
              data-test-id="expert-table-pending-availability-request"
            >
              more_time
            </mat-icon>
          }
        </td>
      </ng-container>
    }
    <ng-container matColumnDef="actions">
      <th *matHeaderCellDef mat-header-cell></th>
      <td
        *matCellDef="let element"
        mat-cell
        data-test-id="expert-table-actions"
      >
        <div class="flex gap-2" [style.min-width]="'48px'">
          <app-screener-btn
            [files]="element.uploadedFiles"
            [viewStage]="viewStage"
          ></app-screener-btn>
        </div>
      </td>
    </ng-container>
    <ng-container matColumnDef="favourited">
      <th *matHeaderCellDef mat-header-cell mat-sort-header="favourited">
        <mat-icon matTooltip="Sort by favorites">star_half</mat-icon>
      </th>
      <td
        *matCellDef="let element"
        mat-cell
        data-test-id="expert-table-favourited"
      >
        <div class="flex gap-2">
          <mat-icon
            class="favourite-star"
            [fontSet]="
              element.favourited ? 'material-icons' : 'material-icons-outlined'
            "
            (click)="onToggleFavourited($event, element)"
          >
            star_rate
          </mat-icon>
        </div>
      </td>
    </ng-container>
    <ng-container matColumnDef="expandedDetail">
      <td
        *matCellDef="let element"
        mat-cell
        [attr.colspan]="columnsToDisplay.length"
      >
        <div
          class="expert-element-detail"
          [class.loading]="expertLoadingIds[element.expertId]"
        >
          @if (expertsExpanded[element.expertId]) {
            <app-expert
              class="full-width"
              [viewStage]="viewStage"
              [opportunity]="opportunity"
              [expertData]="element"
              [isExpanded]="expertsExpanded[element.expertId]"
              [isExpertApprovalRequired]="isExpertApprovalRequired"
              (askFollowUpQuestionSignal)="
                askFollowUpQuestions(element.expertId)
              "
            >
            </app-expert>
          }
        </div>
      </td>
    </ng-container>
    <tr *matHeaderRowDef="columnsToDisplay" mat-header-row></tr>
    <tr
      *matRowDef="
        let element;
        columns: expertsFiltered.data.length ? columnsToDisplay : []
      "
      mat-row
      class="expert-element-row"
      [attr.id]="element.expertId"
      [class.expert-expanded-row]="expertsExpanded[element.expertId]"
      [attr.data-test-id]="'expert-table-expert-summary-' + element.expertId"
      (click)="onToggleExpand(element, expertsExpanded)"
    ></tr>
    <tr
      *matRowDef="let row; columns: ['expandedDetail']"
      mat-row
      class="expert-detail-row"
      [attr.data-test-id]="'expert-table-expert-details-' + row.expertId"
    ></tr>
  </table>
}
